import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    buildOverrideActionRow,
    extractRowActionKey,
    stringifyActionValue,
} from "./seedActionRow";
import { ActionRowDefinition } from "./types";

interface ActionRowsStateParams {
    rows: ActionRowDefinition[];
    valueToAssign: BulkUpdateParams;
}

interface ActionRowsWithNewValuesParams extends ActionRowsStateParams {
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
}

function getImplicitBaseActionKey({
    rows,
    valueToAssign,
}: ActionRowsStateParams): string | undefined {
    const baseRow = rows.find((row) => row.isBaseAction);
    return baseRow
        ? extractRowActionKey(valueToAssign.update, baseRow)
        : undefined;
}

export function buildRowIdToOwnedTxIds({
    rows,
    newValuesMap,
    valueToAssign,
}: ActionRowsWithNewValuesParams): Map<string, number[]> {
    const baseKey = getImplicitBaseActionKey({ rows, valueToAssign });
    const keyToFirstRowId = new Map<string, string>();
    for (const row of rows) {
        if (row.isBaseAction) {
            continue;
        }
        const key = extractRowActionKey(valueToAssign.update, row);
        if (key !== undefined && !keyToFirstRowId.has(key)) {
            keyToFirstRowId.set(key, row.id);
        }
    }

    const out = new Map<string, number[]>();
    for (const [txIdStr, value] of Object.entries(newValuesMap)) {
        const key = stringifyActionValue(valueToAssign.update, value);
        if (baseKey !== undefined && key === baseKey) {
            continue;
        }
        const ownerId = keyToFirstRowId.get(key);
        if (!ownerId) {
            continue;
        }
        const list = out.get(ownerId) ?? [];
        list.push(Number(txIdStr));
        out.set(ownerId, list);
    }
    return out;
}

export function reconcileActionRowsWithNewValues({
    rows,
    newValuesMap,
    valueToAssign,
}: ActionRowsWithNewValuesParams): ActionRowDefinition[] {
    const baseKey = getImplicitBaseActionKey({ rows, valueToAssign });

    const existingKeys = new Set<string>();
    for (const row of rows) {
        const key = extractRowActionKey(valueToAssign.update, row);
        if (key !== undefined) {
            existingKeys.add(key);
        }
    }

    const newRows: ActionRowDefinition[] = [];
    const newKeys = new Set<string>();
    for (const value of Object.values(newValuesMap)) {
        const key = stringifyActionValue(valueToAssign.update, value);
        if (baseKey !== undefined && key === baseKey) {
            continue;
        }
        if (existingKeys.has(key) || newKeys.has(key)) {
            continue;
        }
        newKeys.add(key);
        newRows.push(
            buildOverrideActionRow({
                valueToAssign,
                overrideValue: value,
                source: "auto",
            }),
        );
    }

    const candidate = [...newRows, ...rows];
    const presentKeys = new Set<string>();
    for (const value of Object.values(newValuesMap)) {
        presentKeys.add(stringifyActionValue(valueToAssign.update, value));
    }

    const next = candidate.filter((row) => {
        if (row.source !== "auto") {
            return true;
        }
        if (row.filterScope) {
            return true;
        }
        const key = extractRowActionKey(valueToAssign.update, row);
        if (key === undefined) {
            return true;
        }
        return presentKeys.has(key);
    });

    if (
        next.length === rows.length &&
        next.every((row, index) => row === rows[index])
    ) {
        return rows;
    }

    return next;
}

function promoteRowToBase(row: ActionRowDefinition): ActionRowDefinition {
    return {
        ...row,
        isBaseAction: true,
        source: "base",
        filterScope: undefined,
    };
}

export function removeActionRow(
    rows: ActionRowDefinition[],
    id: string,
): ActionRowDefinition[] {
    if (rows.length <= 1) {
        return rows;
    }

    const targetIndex = rows.findIndex((row) => row.id === id);
    if (targetIndex === -1) {
        return rows;
    }

    const target = rows[targetIndex];
    const remainingRows = rows.filter((row) => row.id !== id);
    if (!target.isBaseAction) {
        return remainingRows;
    }

    const promotedIndex = targetIndex < remainingRows.length ? targetIndex : 0;

    return remainingRows.map((row, index) =>
        index === promotedIndex ? promoteRowToBase(row) : row,
    );
}
